import { Button } from "@/components/ui/baseUi/button"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import {
  PayoutDetailsDownloadModal,
  type PayoutDetailsDownloadModalRef,
} from "@modules/finances/financesPayout/components/payoutDetailsDownloadModal"
import {
  PayoutDetailsHelpModal,
  type PayoutDetailsHelpModalRef,
} from "@modules/finances/financesPayout/components/payoutDetailsHelpModal"
import { PayoutDetailsInfoSkeleton } from "@modules/finances/financesPayout/components/payoutDetailsInfoSkeleton"
import { PayoutStatusWithIcon } from "@modules/finances/financesPayout/components/payoutStatusWithIcon"
import { PayoutTransactionEntity } from "@modules/finances/financesPayout/entities/payoutEntity"
import { PayoutEnumApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { usePayout } from "@modules/finances/financesPayout/hooks/usePayout"
import { BoxTransactionsDetailsLayout } from "@modules/finances/transactions/components/boxTransactionDetailsLayout"
import { UserTransactionsDetailsLayout } from "@modules/finances/transactions/components/userTransactionsDetailsLayout"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuFlag } from "@modules/ui/components/huFlag"
import dayjs from "dayjs"
import { Download } from "lucide-react"
import { Suspense, useEffect, useRef, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

type PayoutDetailsInfoProps = {
  payoutEnumApi: PayoutEnumApi
  payoutId: string
  className?: string
} & React.ComponentProps<"div">

export function PayoutDetailsInfo({ payoutEnumApi, payoutId, className, ...props }: PayoutDetailsInfoProps) {
  const language = useLanguage()
  const payoutDetailsHelpModal = useRef<PayoutDetailsHelpModalRef>(null)
  const payoutDetailsDownloadModal = useRef<PayoutDetailsDownloadModalRef>(null)
  const { data: payout, isLoading } = usePayout(payoutEnumApi, payoutId)
  const { sidebarNavigateTo, setCurrentStackState, getCurrentStackState } = useSidebar()
  const [scrollPosition, setScrollPosition] = useState(0)
  const [scrollPositionTransactionId, setScrollPositionTransactionId] = useState<string | null>(null)
  const containerScrollRef = useRef<HTMLDivElement>(null)

  const summaryHeaders = [
    { label: word("finances.payoutDetails.summaryHeaders.quantity"), col: "col-span-1" },
    { label: word("finances.payoutDetails.summaryHeaders.gross"), col: "col-span-2" },
    { label: word("finances.payoutDetails.summaryHeaders.fees"), col: "col-span-2" },
    { label: word("finances.payoutDetails.summaryHeaders.total"), col: "col-span-2" },
  ]
  const salesHeaders = [
    { label: word("finances.payoutDetails.salesHeaders.type"), col: "col-span-3" },
    { label: word("finances.payoutDetails.salesHeaders.gross"), col: "col-span-2" },
    { label: word("finances.payoutDetails.salesHeaders.net"), col: "col-span-2" },
    { label: word("finances.payoutDetails.salesHeaders.name"), col: "col-span-3" },
    { label: word("finances.payoutDetails.salesHeaders.date"), col: "col-span-2" },
  ]

  const bgSelectedPayout = PayoutEnumApi.OWNER === payoutEnumApi ? "bg-electricPurple-800" : "bg-leGreen-300"

  const handleClickTransaction = async (transaction: PayoutTransactionEntity) => {
    if (!transaction.transactionId) return
    setCurrentStackState("scrollPositionPayoutId", payoutId)
    setCurrentStackState("scrollPositionTransactionId", transaction.transactionId)
    setCurrentStackState("scrollPosition", scrollPosition.toString())

    await sidebarNavigateTo({
      content:
        payoutEnumApi === PayoutEnumApi.OWNER ? (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <BoxTransactionsDetailsLayout transactionId={transaction.transactionId} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <UserTransactionsDetailsLayout transactionId={transaction.transactionId} />
            </Suspense>
          </ErrorBoundary>
        ),
      replaceAllComponents: false,
    })
  }

  const handleScroll = () => {
    if (containerScrollRef.current) {
      setScrollPosition(containerScrollRef.current.scrollTop)
    }
  }

  useEffect(() => {
    if (!isLoading) {
      const scrollTo = getCurrentStackState("scrollPosition")
      setScrollPositionTransactionId(getCurrentStackState("scrollPositionTransactionId") ?? null)
      containerScrollRef.current?.scrollTo(0, scrollTo ? parseInt(scrollTo, 10) : 0)
    }
  }, [isLoading])

  if (!payout || isLoading) {
    return <PayoutDetailsInfoSkeleton />
  }

  const payoutTransactionSummary = {
    payment: { label: word("finances.payoutDetails.transactionSummary.payment"), transaction: payout.paymentSummary },
    refund: { label: word("finances.payoutDetails.transactionSummary.refund"), transaction: payout.refundSummary },
    adjustment: {
      label: word("finances.payoutDetails.transactionSummary.adjustement"),
      transaction: payout.adjustmentSummary,
    },
  }

  return (
    <>
      <div className={cn(className)} {...props}>
        {/* HEADER */}
        <div className="relative flex w-full justify-between rounded-b-4xl bg-surface-foreground px-7 pt-14 pb-6">
          {/* Column paid */}
          <div className="flex flex-col justify-between">
            <div className="flex flex-col gap-1">
              <Text fontStyle="HS" color="text-textDefault-heading">
                {formatCurrency(payout.amount, payout.currency, language)}
              </Text>
              <PayoutStatusWithIcon payout={payout} size={24} color="text-textSuccess" fontStyle="BM" />
            </div>
            {payout.bankName && payout.last4Digits && (
              <div className="mt-4 flex flex-col gap-1">
                <Text fontStyle="LS" color="text-textDefault-caption">
                  {word("finances.payoutDetails.accountInformation")}
                </Text>
                <div className="flex flex-wrap justify-between gap-2">
                  <Text color="text-textDefault-heading" fontStyle="BM">
                    {`${payout.bankName} ${payout.getFormatedDigits()}`}
                  </Text>
                  {payout.bankCountry && (
                    <div className="flex items-center gap-1">
                      <Text color="text-textDefault-heading" fontStyle="BM">
                        {payout.bankCountry}
                      </Text>
                      <HuFlag
                        className="flex rounded-2xl border border-neutral-700"
                        countryCode={payout.bankCountry || ""}
                        svg={true}
                        style={{ width: "1.4rem", height: "1.2rem", objectFit: "cover" }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Column timeline */}
          <div className="flex flex-col">
            <Text fontStyle="BS" color="text-textDefault-caption" className="mb-3">
              {word("finances.payoutDetails.timeline")}
            </Text>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <div className="flex size-4 rounded-md bg-primary-500"></div>
                <div className="flex flex-col gap-1">
                  <Text fontStyle="LM" color="text-textDefault-caption">
                    {word("finances.payoutDetails.transferMade")}
                  </Text>
                  <Text fontStyle="TS" color="text-textDefault-heading">
                    {dayjs(payout.createdAt).format("ll")}
                  </Text>
                </div>

                <Button
                  variant="underline"
                  className="pt-0"
                  onClick={() => {
                    payoutDetailsHelpModal.current?.open()
                  }}
                >
                  {word("global.help")}
                </Button>
              </div>

              {payout.intervalStartDate && payout.intervalEndDate && (
                <div className="flex gap-2">
                  <div className="flex size-4 rounded-md bg-neutral-600"></div>
                  <div className="flex flex-col gap-1">
                    <Text fontStyle="LS" color="text-textDefault-caption">
                      {word("finances.payoutDetails.transferInTransit")}
                    </Text>
                    <Text fontStyle="LM" color="text-textDefault-heading">
                      {dayjs(payout.intervalStartDate).format("ll")} - {dayjs(payout.intervalEndDate).format("ll")}
                    </Text>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <div className="flex size-4 rounded-md bg-neutral-600"></div>
                <div className="flex flex-col gap-1">
                  <Text fontStyle="LS" color="text-textDefault-caption">
                    {word("finances.payoutDetails.automatedTransfer")}
                  </Text>
                  <Text fontStyle="LM" color="text-textDefault-heading">
                    {dayjs(payout.expectedReceptionDate).format("lll")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/*CONTENT */}
        <div className="flex w-full flex-col px-6">
          {/* SUMMARY */}
          <div className="mt-6 flex w-full flex-col">
            <Text fontStyle="LM" color="text-textDefault-caption">
              {word("finances.payoutDetails.headerTab.summary").toUpperCase()}
            </Text>
            <div className="grid grid-cols-10">
              <div className="col-span-3"></div>
              {summaryHeaders.map((header) => (
                <div className={header.col} key={`${header.label}_SUMMARY`}>
                  <Text fontStyle="LM" color="text-textDefault-caption">
                    {header.label}
                  </Text>
                </div>
              ))}
            </div>
            <Separator />
            {Object.values(payoutTransactionSummary).map((summary) => (
              <div className="flex w-full flex-col pt-2" key={summary.label}>
                <div className="grid grid-cols-10 items-end">
                  <div className="col-span-3">
                    <Text fontStyle="LM" className="leading-normal text-textDefault-heading">
                      {summary.label}
                    </Text>
                  </div>
                  <div className="col-span-1">
                    <Text fontStyle="BM" className="break-all text-textDefault-heading">
                      {summary.transaction.number}
                    </Text>
                  </div>
                  <div className="col-span-2">
                    <Text fontStyle="BM" className="break-all text-textDefault-heading">
                      {formatCurrency(summary.transaction.totalAmount ?? 0, payout.currency, language)}
                    </Text>
                  </div>
                  <div className="col-span-2">
                    <Text fontStyle="BM" className="break-all text-textDefault-heading">
                      {formatCurrency(summary.transaction.feeAmount ?? 0, payout.currency, language)}
                    </Text>
                  </div>
                  <div className="col-span-2">
                    <Text fontStyle="BM" className="break-all text-textDefault-heading">
                      {formatCurrency(summary.transaction.getTotalLessFee(), payout.currency, language)}
                    </Text>
                  </div>
                </div>
                <Separator />
              </div>
            ))}
          </div>
          {/* TRANSFER */}
          <div className="mt-3 flex w-full items-baseline justify-end gap-4">
            <Text fontStyle="LM" color="text-textDefault-heading">
              {word("finances.payoutDetails.transfer")}
            </Text>
            <Text fontStyle="BM" color="text-textDefault-heading">
              {formatCurrency(payout.amount, payout.currency, language)}
            </Text>
          </div>
          {/* SALES */}
          <div className="mt-6 flex w-full flex-col">
            <div className="mb-3 flex w-full items-center justify-between">
              <Text fontStyle="LM" color="text-textDefault-caption">
                {word("finances.payoutDetails.headerTab.sales").toUpperCase()}
              </Text>

              <Button
                variant="primary"
                onClick={() => {
                  payoutDetailsDownloadModal.current?.open()
                }}
              >
                {word("global.download")}
                <Download size={15} />
              </Button>
            </div>

            <div className="grid w-full grid-cols-10 px-2">
              {salesHeaders.map((header) => (
                <div className={header.col} key={`${header.label}_SALES`}>
                  <Text fontStyle="LM" className="break-all text-textDefault-caption">
                    {header.label}
                  </Text>
                </div>
              ))}
            </div>
            <Separator />
            <div className="h-80 w-full overflow-y-scroll" onScroll={handleScroll} ref={containerScrollRef}>
              {payout.transactions.map((transaction) => (
                <div
                  className={`flex w-full flex-col px-2 ${
                    scrollPositionTransactionId === transaction.transactionId ? bgSelectedPayout : "hover:surface-hover"
                  }`}
                  key={transaction.transactionId}
                >
                  <div
                    className={`grid w-full grid-cols-10 items-start pt-3 ${transaction.transactionId ? "cursor-pointer" : ""}`}
                    onClick={() => void handleClickTransaction(transaction)}
                  >
                    <div className="col-span-3">
                      <Text fontStyle="BS" className="break-all text-textDefault-heading">
                        {transaction.getWordBalanceTransactionStatus()}
                      </Text>
                    </div>
                    <div className="col-span-2">
                      <Text fontStyle="BM" className="break-word text-textDefault-heading">
                        {formatCurrency(transaction.amount, transaction.currency, language)}
                      </Text>
                    </div>
                    <div className="col-span-2">
                      <div className="flex flex-col gap-1">
                        <Text fontStyle="BM" className="break-word text-textDefault-heading">
                          {formatCurrency(transaction.amount - transaction.fee, transaction.currency, language)}
                        </Text>
                        <Text fontStyle="LS" className="break-word text-textDefault-caption">
                          {`-${formatCurrency(transaction.fee, transaction.currency, language)}`}
                        </Text>
                      </div>
                    </div>
                    <div className="col-span-3">
                      <Text fontStyle="BS" className="break-word text-textDefault-heading">
                        {transaction.name ?? "-"}
                      </Text>
                    </div>
                    <div className="col-span-2">
                      <Text fontStyle="BS" className="break-word text-textDefault-heading capitalize">
                        {dayjs(transaction.createdAt).format("MMM DD")}
                      </Text>
                    </div>
                  </div>
                  <Separator />
                </div>
              ))}
            </div>
          </div>
          {/* FOOTER */}
          <div className="mt-6 mb-4 flex w-full flex-col gap-2">
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" color="text-textDefault-caption">
                {word("finances.payoutDetails.internalNote")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading capitalize">
                {payout.note?.toLowerCase() ?? "-"}
              </Text>
            </div>
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" color="text-textDefault-caption">
                {word("finances.payoutDetails.transferMethod")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading capitalize">
                {payout.transferMethod.toLowerCase()}
              </Text>
            </div>
            <div className="flex flex-col gap-1">
              <Text fontStyle="BS" color="text-textDefault-caption">
                {word("finances.payoutDetails.transferTrackingId")}
              </Text>
              <Text fontStyle="TS" className="text-textDefault-heading">
                {payout.transferTrackingId ?? "-"}
              </Text>
            </div>
          </div>
        </div>
      </div>
      <PayoutDetailsHelpModal payoutDetails={payout} ref={payoutDetailsHelpModal} />
      <PayoutDetailsDownloadModal
        payoutDetails={payout}
        payoutEnumApi={payoutEnumApi}
        ref={payoutDetailsDownloadModal}
      />
    </>
  )
}
